<script setup lang="ts">
import PageTitle from '@/components/page-title.vue'
import PageNavigation from '@/components/page-navigation.vue'
import PageContent from '@/components/page-content.vue'
import { computed, ref } from 'vue'
import { useLinkedMeters } from '@/composables/assets/meters'
import ModalContainer from '@/components/modal/modal-container.vue'
import ModalTitle from '@/components/modal/modal-title.vue'
import ModalContent from '@/components/modal/modal-content.vue'
import { faCheck, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons'
import ModalFooter from '@/components/modal/modal-footer.vue'
import ButtonPrimary from '@/components/button-primary.vue'
import { useAuthenticatedUser } from '@/shared/composables/useAuthenticatedUser'
import { AccessArea } from '@/shared/constants'
import LinkText from '@/shared/components/LinkText.vue'
import AssetYieldsStatus from '@/features/assets/components/AssetYieldsStatus.vue'
import { AssetDeliveryStatus } from '@/features/assets/constants'
import { useQuery } from '@tanstack/vue-query'
import { AssetQueries } from '@/features/assets/queries'

const props = defineProps<{ assetCode: string }>()

const { data: assetDetails } = useQuery(AssetQueries.fetch(() => props.assetCode))
const { data: linkedMeters } = useLinkedMeters(() => props.assetCode)
const { hasPermission } = useAuthenticatedUser()

const isOperational = computed(
  () => assetDetails.value?.deliveryStatus === AssetDeliveryStatus.Operational
)

const hasLinkedMeters = computed(
  () => (linkedMeters.value?.length ?? 0) > 0
)

const showPopup = ref(false)

function showPopupIf(value: boolean) {
  console.log('showPopupIf()')
  if (value) {
    showPopup.value = true
  }
}
</script>

<template>
  <PageContent>
    <PageTitle v-if="assetDetails">
      {{ assetDetails?.projectName }}

      <template #subtitle>(Project: {{ assetDetails.projectCode }} | Asset: {{ assetDetails.code }})</template>
    </PageTitle>

    <div class="d-flex justify-content-between align-items-center">
      <PageNavigation>
        <LinkText :to="{ name: 'assets.meters', params: { assetCode } }">Link Meters</LinkText>
        <LinkText :to="{ name: 'assets.settings', params: { assetCode } }" :disabled="!hasLinkedMeters" @click="showPopupIf(!hasLinkedMeters)">Settings</LinkText>
        <LinkText :to="{ name: 'assets.yields', params: { assetCode } }">Yields</LinkText>
        <LinkText :to="{ name: 'assets.setupInvoice', params: { assetCode } }" :disabled="!isOperational" @click="showPopupIf(!isOperational)">Manage Invoice</LinkText>
      </PageNavigation>

      <AssetYieldsStatus v-if="$route.name === 'assets.yields'" :asset-code="assetCode"  />
    </div>

    <ModalContainer v-if="showPopup">
      <ModalTitle>Ensure all required info is provided</ModalTitle>
      <ModalContent :icon="faTriangleExclamation" icon-variant="warning">
        <p>The following should please be completed to proceed:</p>

        <p>
          <b>STEP 1:</b> Link meters associated with this asset.
          <br>
          <b>STEP 2:</b> Complete all inputs on the Settings tab.
          <br>
          <b>STEP 3:</b> Set up the Invoice Template with all recurring charges.
        </p>

        <p>
          If meters were linked, they should auto-fill the Invoice Template. Review the displayed
          information to confirm accuracy for future invoices.
        </p>
      </ModalContent>
      <ModalFooter class="text-end">
        <ButtonPrimary :icon="faCheck" @click="showPopup = false">Okay</ButtonPrimary>
      </ModalFooter>
    </ModalContainer>

    <RouterView />
  </PageContent>
</template>
